/**
 * A photo's own crop ("Only the selected photo", imageEditorBulk) — redesign 2026-10-05 after QA round 3
 * (SS3-1 an own crop leaked to the other photos; SS3-2 with the batch rotated it changed on a thumbnail
 * switch and the ZIP got the wrong crop). Design by construction:
 *  - the batch holds ONE ops list (batch.ts); an own crop is stored ONLY on its photo's BatchItem as a
 *    rectangle in that photo's own SOURCE pixels (before any op), never in view/rotated space and never in
 *    shared state;
 *  - a photo's pipeline (view = export) is the batch's ops as they land on it with every crop step removed
 *    and the own crop put before the first resize that follows the batch crop's place (the start when the
 *    batch has no crop), else last (review9 batch-1: always LAST, a typed W 1080 sized the uncropped photo and
 *    the 1:1 came out 810 x 810), mapped forward from source pixels through the ops before it — so it covers
 *    the same source pixels whatever the batch's rotation/flip/size does, and an own 1:1 gives the size a batch
 *    1:1 in the same place gives. The own crop never goes before a turn or flip (the Crop tool keeps showing the
 *    turned photo and a ratio chip keeps its orientation): with one after the resize it goes after it; a platform size (1080 x 1350) with
 *    no batch crop stays last: that exact size is what was asked for;
 *  - an edit of that crop maps the box back to source pixels; selection never writes anything.
 * The op carries `own: { fixed }` so the other tools' "current op" looks past it (edit-stack currentIndex) and a
 * resize/rotate/flip on this photo still edits the batch's op instead of appending a second one — never an op
 * that sits before a batch crop (that would move the crop on the other photos; a new op is appended instead).
 */
import { EditState, Op, CropOp, Rect, Size, walk, cropRectPx, clampRect, sizeAfter, isIdentity } from './edit-stack';

export interface OwnCrop {
    /** The box in the photo's SOURCE pixels (pre-rotate). A photo not sized here uses a 1 x 1 stand-in. */
    rect: Rect;
    /** The ratio lock as drawn, and whether an odd number of quarter turns preceded it then. */
    aspect: number | null;
    aspectOdd: boolean;
    shape: 'rect' | 'circle';
    platform: Size | null;
}

// ---------------------------------------------------------------- affine maps (same convention as render.ts)
type Affine = [number, number, number, number, number, number];
const ID: Affine = [1, 0, 0, 1, 0, 0];
const mul = (A: Affine, B: Affine): Affine => [A[0] * B[0] + A[2] * B[1], A[1] * B[0] + A[3] * B[1], A[0] * B[2] + A[2] * B[3], A[1] * B[2] + A[3] * B[3], A[0] * B[4] + A[2] * B[5] + A[4], A[1] * B[4] + A[3] * B[5] + A[5]];
const tr = (x: number, y: number): Affine => [1, 0, 0, 1, x, y];
const sc = (x: number, y = x): Affine => [x, 0, 0, y, 0, 0];
function opAffine(op: Op, inPx: Size, outPx: Size): Affine {
    switch (op.type) {
        case 'crop': { const r = cropRectPx(op, inPx); const t = tr(-r.x, -r.y); return op.platform ? mul(sc(outPx.width / r.width, outPx.height / r.height), t) : t; }
        case 'resize': return sc(outPx.width / inPx.width, outPx.height / inPx.height);
        case 'rotate': { const a = (op.quarterTurns * 90 + op.fineAngle) * Math.PI / 180; const c = Math.cos(a), s = Math.sin(a); return mul(tr(outPx.width / 2, outPx.height / 2), mul([c, s, -s, c, 0, 0], tr(-inPx.width / 2, -inPx.height / 2))); }
        case 'flip': return mul(tr(op.h ? inPx.width : 0, op.v ? inPx.height : 0), sc(op.h ? -1 : 1, op.v ? -1 : 1));
    }
}
/** Source pixels -> the pixels after the first `n` ops. */
function prefix(s: EditState, n: number): Affine {
    let m = ID; for (const st of walk(s, n)) m = mul(opAffine(st.op, st.inPx, st.outPx), m); return m;
}
function invert(m: Affine): Affine {
    const det = m[0] * m[3] - m[1] * m[2];
    const a = m[3] / det, b = -m[1] / det, c = -m[2] / det, d = m[0] / det;
    return [a, b, c, d, -(a * m[4] + c * m[5]), -(b * m[4] + d * m[5])];
}
const snap = (n: number) => Math.round(n * 1e6) / 1e6;   // kill cos(90°) dust before rounding to pixels
function mapRect(m: Affine, r: Rect): Rect {
    const xs: number[] = [], ys: number[] = [];
    for (const [x, y] of [[r.x, r.y], [r.x + r.width, r.y], [r.x, r.y + r.height], [r.x + r.width, r.y + r.height]]) {
        xs.push(snap(m[0] * x + m[2] * y + m[4])); ys.push(snap(m[1] * x + m[3] * y + m[5]));
    }
    const x0 = Math.round(Math.min(...xs)), y0 = Math.round(Math.min(...ys));
    return { x: x0, y: y0, width: Math.round(Math.max(...xs)) - x0, height: Math.round(Math.max(...ys)) - y0 };
}
const oddTurns = (ops: Op[]) => ops.reduce((n, op) => n + (op.type === 'rotate' ? op.quarterTurns : 0), 0) % 2 === 1;
/** The size own-crop maths works in: the photo's pixels, or a 1 x 1 stand-in when it was not sized here. */
export const workSize = (s: Size): Size => (s.width > 0 && s.height > 0 ? s : { width: 1, height: 1 });

/** The whole photo as an own crop (nothing cut yet). */
export function fullOwnCrop(source: Size): OwnCrop {
    const s = workSize(source);
    return { rect: { x: 0, y: 0, width: s.width, height: s.height }, aspect: null, aspectOdd: false, shape: 'rect', platform: null };
}

/** The crop op at index `j` of `view` (a pipeline on this photo) as an own crop in source pixels. */
export function ownCropFromView(view: EditState, j: number): OwnCrop {
    const src = workSize(view.source); const s = { ...view, source: src };
    const op = s.ops[j] as CropOp;
    const box = cropRectPx(op, sizeAfter(s, j));
    const r = mapRect(invert(prefix(s, j)), box);
    const rect = clampRect(r, src);
    return { rect, aspect: op.aspect, aspectOdd: oddTurns(s.ops.slice(0, j)), shape: op.shape, platform: op.platform ? { ...op.platform } : null };
}

/**
 * The photo's pipeline with its own crop: `base` (the batch's ops as they land on this photo) with every
 * crop step removed and the own crop inserted at `ownCropIndex`, mapped from source pixels through the ops
 * before it.
 */
export function withOwnCrop(base: EditState, own: OwnCrop): EditState {
    const src = workSize(base.source);
    const ops = base.ops.filter(op => op.type !== 'crop');
    const at = ownCropIndex(base.ops, own);
    const pre: EditState = { source: src, ops: ops.slice(0, at) };
    const input = sizeAfter(pre, at);
    const r = clampRect(mapRect(prefix(pre, at), own.rect), input);
    const aspect = own.aspect == null ? null : oddTurns(pre.ops) === own.aspectOdd ? own.aspect : 1 / own.aspect;
    const crop: CropOp = { type: 'crop', f: { x: r.x / input.width, y: r.y / input.height, w: r.width / input.width, h: r.height / input.height },
        aspect, shape: own.shape, platform: own.platform ? { ...own.platform } : null, own: { fixed: fixedOps(base.ops) } };
    return { source: base.source, ops: [...ops.slice(0, at), crop, ...ops.slice(at)] };
}

/** Where the own crop goes among the batch's non-crop ops: after every turn and flip (so the Crop tool shows the
 *  turned photo and a 4:5 chip is 4:5 in the output - review10: W 1080, Rotate 90, own 4:5 came out 1350 x 1080
 *  when the crop went before the turn), and there before the first resize at or after the batch's last crop's
 *  place (the start with no batch crop), so a typed width sizes the cropped region as a batch crop there does;
 *  with no such resize, last. A platform size with no batch crop stays last (its exact pixels are the request). */
function ownCropIndex(ops: Op[], own: OwnCrop): number {
    const nonCrop = ops.filter(op => op.type !== 'crop');
    const hasCrop = ops.some(op => op.type === 'crop');
    if (!hasCrop && own.platform) return nonCrop.length;
    let from = hasCrop ? fixedOps(ops) : 0;
    for (let j = nonCrop.length - 1; j >= from; j--) if ((nonCrop[j].type === 'rotate' || nonCrop[j].type === 'flip') && !isIdentity(nonCrop[j])) { from = j + 1; break; }
    for (let j = from; j < nonCrop.length; j++) if (nonCrop[j].type === 'resize' && !isIdentity(nonCrop[j])) return j;
    return nonCrop.length;
}

/** An own crop that cuts nothing: the whole photo, free ratio, a rectangle, no platform size (review9 batch-5:
 *  one Undo after "Only the selected photo" + 1:1 left this behind, badged "own crop" yet detached from later
 *  batch crops). It counts as no own crop: the photo shows and exports the batch. */
export function isFreeFullOwnCrop(own: OwnCrop | null, source: Size | null): boolean {
    if (!own) return false;
    const s = workSize(source ?? { width: 0, height: 0 }); const r = own.rect;
    return own.aspect == null && own.shape === 'rect' && !own.platform && r.x <= 0 && r.y <= 0 && r.x + r.width >= s.width && r.y + r.height >= s.height;
}
/** Whether a photo has an own crop that changes anything (the "own crop" tag, "N have their own crop"). */
export function hasOwnCrop(own: OwnCrop | null, source: Size | null): boolean { return !!own && !isFreeFullOwnCrop(own, source); }

/** How many non-crop ops precede the batch's last crop: on this photo they cannot be a tool's current op
 *  (and, with a batch crop, where the own crop sits). */
function fixedOps(ops: Op[]): number {
    let last = ops.length - 1; while (last >= 0 && ops[last].type !== 'crop') last--;
    return ops.slice(0, Math.max(0, last)).filter(op => op.type !== 'crop').length;
}

/** Same box, same lock: two own crops that export the same pixels. */
export function sameOwnCrop(a: OwnCrop | null, b: OwnCrop | null): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}
